import axios, { AxiosError } from "axios";
import { useContext } from "react";
import useSWR from "swr";
import { pollingInterval } from "../../../constants/pollingInterval";
import { AuthContext } from "../../../context/AuthContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import {
  IAccountsIsolatedHealth,
  IGlobalIsolatedHealthPosition,
  IGlobalIsolatedHealthSummary,
  IsolatedRiskStatus,
} from "../../../interfaces/aevo";
import { ADMIN_API_URL } from "../../../utils/env";

interface IAccountsIsolatedHealthApiResponse {
  summary?: Partial<IGlobalIsolatedHealthSummary>;
  positions?: Array<Partial<IGlobalIsolatedHealthPosition>>;
}

const RISK_STATUSES: IsolatedRiskStatus[] = [
  "healthy",
  "warning",
  "breached",
  "liquidating",
];

const isRiskStatus = (value: unknown): value is IsolatedRiskStatus =>
  typeof value === "string" &&
  RISK_STATUSES.includes(value as IsolatedRiskStatus);

const asString = (value: unknown, fallback: string = "0"): string => {
  if (value === null || value === undefined) {
    return fallback;
  }

  return String(value);
};

const asNumber = (value: unknown): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const normalizePosition = (
  position: Partial<IGlobalIsolatedHealthPosition>
): IGlobalIsolatedHealthPosition => ({
  account: asString(position.account, "-"),
  instrument_id: asNumber(position.instrument_id),
  instrument_name: asString(position.instrument_name, "-"),
  asset: asString(position.asset, "-"),
  position: asString(position.position),
  entry_price: asString(position.entry_price),
  mark_price: asString(position.mark_price),
  allocated_isolated_margin: asString(position.allocated_isolated_margin),
  base_isolated_margin: asString(position.base_isolated_margin),
  upnl: asString(position.upnl),
  maintenance_margin: asString(position.maintenance_margin),
  liquidation_price: asString(position.liquidation_price, "-"),
  risk_status: isRiskStatus(position.risk_status) ?
    position.risk_status :
    "healthy",
});

const normalizeSummary = (
  summary?: Partial<IGlobalIsolatedHealthSummary>
): IGlobalIsolatedHealthSummary => ({
  isolated_positions_count: asNumber(summary?.isolated_positions_count),
  warning_positions_count: asNumber(summary?.warning_positions_count),
  breached_positions_count: asNumber(summary?.breached_positions_count),
  liquidating_positions_count: asNumber(summary?.liquidating_positions_count),
});

const normalizeResponse = (
  payload: IAccountsIsolatedHealthApiResponse
): IAccountsIsolatedHealth => ({
  summary: normalizeSummary(payload.summary),
  positions: (payload.positions || []).map(normalizePosition),
});

export const useAccountsIsolatedHealth = () => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const endpoint = ADMIN_API_URL(AdminEndpointEnum.ACCOUNTS_ISOLATED_HEALTH);

  const swr = useSWR<IAccountsIsolatedHealth, AxiosError>(
    tokenId ? [endpoint, tokenId] : undefined,
    async () => {
      const response = await axios.get<IAccountsIsolatedHealthApiResponse>(
        endpoint,
        {
          ...queryConfig(),
        }
      );

      return normalizeResponse(response.data);
    },
    {
      refreshInterval: pollingInterval[AdminEndpointEnum.ACCOUNTS_ISOLATED_HEALTH],
    }
  );

  return {
    ...swr,
  };
};
